import "@/i18n";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster } from "sonner";

import { AppProvider, useApp } from "@/context/AppContext";
import Layout from "@/components/Layout";
import CookieConsent from "@/components/CookieConsent";
import ForceChangePasswordModal from "@/components/ForceChangePasswordModal";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import StoresPage from "@/pages/StoresPage";
import FlowPage from "@/pages/FlowPage";
import AuditPage from "@/pages/AuditPage";
import FindingsPage from "@/pages/FindingsPage";
import FollowUpPage from "@/pages/FollowUpPage";
import SummaryPage from "@/pages/SummaryPage";
import AuditsListPage from "@/pages/AuditsListPage";
import ChecklistPage from "@/pages/ChecklistPage";
import PublicSummaryPage from "@/pages/PublicSummaryPage";
import PublicFindingPage from "@/pages/PublicFindingPage";
import PublicChecklistPage from "@/pages/PublicChecklistPage";
import UsersPage from "@/pages/UsersPage";
import PermissionsPage from "@/pages/PermissionsPage";
import SmsTermsPage from "@/pages/SmsTermsPage";
import SmsOptInPage from "@/pages/SmsOptInPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import AssignmentsPage from "@/pages/AssignmentsPage";
import AssignmentDetailPage from "@/pages/AssignmentDetailPage";
import GamesPage from "@/pages/GamesPage";
import GamePlayPage from "@/pages/GamePlayPage";
import PublicGamePage from "@/pages/PublicGamePage";
import GameHostPage from "@/pages/GameHostPage";
import GameJoinPage from "@/pages/GameJoinPage";

function ProtectedRoute({ children, roles }) {
  const { user, hydrated } = useApp();
  const { t } = useTranslation();
  if (!hydrated) {
    return (
      <div className="min-h-screen grid place-items-center bg-white text-stone-500">
        {t("common.loading")}
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, hydrated } = useApp();
  if (!hydrated) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><LoginPage /></PublicOnly>} />
      <Route path="/share/:token" element={<PublicSummaryPage />} />
      <Route path="/finding/:token" element={<PublicFindingPage />} />
      <Route path="/checklist/:token" element={<PublicChecklistPage />} />
      <Route path="/play/:token" element={<PublicGamePage />} />
      <Route path="/join" element={<GameJoinPage />} />
      <Route path="/join/:code" element={<GameJoinPage />} />
      <Route path="/sms-terms" element={<SmsTermsPage />} />
      <Route path="/sms-optin" element={<SmsOptInPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/games/:id/play" element={<ProtectedRoute><GamePlayPage /></ProtectedRoute>} />
      <Route path="/games/:id/host" element={<ProtectedRoute roles={["admin", "direccion"]}><GameHostPage /></ProtectedRoute>} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/stores" element={<StoresPage />} />
        <Route path="/flow" element={<ProtectedRoute roles={["admin", "auditor"]}><FlowPage /></ProtectedRoute>} />
        <Route path="/checklist" element={<ProtectedRoute roles={["admin", "auditor"]}><ChecklistPage /></ProtectedRoute>} />
        <Route path="/findings" element={<FindingsPage />} />
        <Route path="/follow-up" element={<ProtectedRoute roles={["admin", "gerente", "direccion"]}><FollowUpPage /></ProtectedRoute>} />
        <Route path="/audits" element={<AuditsListPage />} />
        <Route path="/audit/:id" element={<ProtectedRoute roles={["admin", "auditor"]}><AuditPage /></ProtectedRoute>} />
        <Route path="/summary/:id" element={<SummaryPage />} />
        <Route path="/users" element={<ProtectedRoute roles={["admin"]}><UsersPage /></ProtectedRoute>} />
        <Route path="/permissions" element={<ProtectedRoute roles={["admin"]}><PermissionsPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/assignments" element={<AssignmentsPage />} />
        <Route path="/assignments/:id" element={<AssignmentDetailPage />} />
        <Route path="/games" element={<GamesPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AppRoutes />
        <ForceChangePasswordModal />
        <CookieConsent />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AppProvider>
  );
}
